import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type { Site, Space } from '@gitbook/api-client';
import {
    type AnalyticsContextSpaceContent,
    createPageEvent,
    logEvent,
} from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import {
    createPalette,
    PaletteCommandType,
    type PaletteDef,
    PalettePositionMaxHeight,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import {
    EditContentSourceDialog,
    ImportInSpaceDialog,
    useContentNavigation,
    usePublicSitesPalette,
    useSiteAudienceCommand,
    useSpaceSites,
} from '../../../Components';
import { apiQuery } from '../../../state';
import { useSpaceContext } from '../context';
import { useExportPDFCommand } from '../utils';
import { DeletePageDialog } from './DeletePageDialog';
import { EditPageDialog } from './EditPageDialog';
import { useInsertPageCommands } from './useInsertPageCommands';

/**
 * Props added to the wrapped component.
 */
type WithPageCommandsProps = {
    commandsPalette: () => PaletteDef;
    commandsElement: React.ReactElement;
    siteAudience: ReturnType<typeof useSiteAudienceCommand> | null;
};

/**
 * Props passed to the wrapped component.
 */
type WrappedComponentProps = object & {
    readOnly: boolean;
    space: Space;
    page: hiveCore.RevisionPage;
    analyticsContext: AnalyticsContextSpaceContent;
    adaptiveContentContext: {
        site: Site;
        organizationId: string;
    } | null;
};

type PageCommandsProps = WrappedComponentProps & {
    Component: React.ComponentType<any>;
};

export function withPageCommands<Props extends WrappedComponentProps>(
    WrappedComponent: React.ComponentType<Props & WithPageCommandsProps>
): React.FC<Props> {
    return (props: Props) =>
        props.adaptiveContentContext ? (
            <PageCommandsWithSite
                {...props}
                adaptiveContentContext={props.adaptiveContentContext}
                Component={WrappedComponent}
            />
        ) : (
            <PageCommands {...props} siteAudience={null} Component={WrappedComponent} />
        );
}

function PageCommandsWithSite(
    props: PageCommandsProps & {
        adaptiveContentContext: NonNullable<WrappedComponentProps['adaptiveContentContext']>;
    }
) {
    const { space, page, readOnly, adaptiveContentContext, analyticsContext } = props;

    const hiveBranch = useHiveBranch();
    const result = useSiteAudienceCommand({
        organizationId: adaptiveContentContext.organizationId,
        site: adaptiveContentContext.site,
        target: 'page',
        readOnly,
        condition: page.condition ?? undefined,
        onChange: async (condition) => {
            if (!page.condition && !condition) {
                return;
            }

            const isConditionRemoved = page.condition && !condition;
            const isConditionEdited = !!page.condition && !!condition;
            const isConditionAdded = !page.condition && !!condition;

            assertIsDefined(hiveBranch);

            hiveBranch.change((change) => {
                if (isConditionRemoved) {
                    change.removePageCondition(page.id);
                } else if (isConditionEdited || isConditionAdded) {
                    change.updatePageCondition(page.id, condition);
                }
            });

            assertIsDefined(analyticsContext);
            assertIsDefined(space);

            if (isConditionRemoved) {
                logEvent(
                    createPageEvent(space, page, {
                        ...analyticsContext,
                        type: 'edit_remove_condition',
                    })
                );
            } else if (isConditionEdited) {
                logEvent(
                    createPageEvent(space, page, {
                        ...analyticsContext,
                        type: 'edit_edit_condition',
                        condition,
                    })
                );
            } else if (isConditionAdded) {
                logEvent(
                    createPageEvent(space, page, {
                        ...analyticsContext,
                        type: 'edit_add_condition',
                        condition,
                    })
                );
            }
        },
    });

    return <PageCommands {...props} siteAudience={result} />;
}

function PageCommands(
    props: PageCommandsProps & {
        siteAudience: ReturnType<typeof useSiteAudienceCommand> | null;
    }
) {
    const { readOnly, page, siteAudience, Component } = props;
    const { revision } = useHiveRevision();
    const { space, changeRequest, analyticsContext } = useSpaceContext();

    const [modal, setModal] = React.useState<'delete' | 'edit' | 'export' | null>(null);
    const hiveBranch = useHiveBranch();
    const { getNavigationHref, navigateToPage } = useContentNavigation();

    const [insertElement, insertPalette, insertPage] = useInsertPageCommands({
        parent: page.id,
    });

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: space.organization,
        })
    );

    const sites = useSpaceSites(organizationContent, space.id);
    const publicPageCommands = usePublicSitesPalette({
        sites,
        revision,
        page,
        changeRequest,
    });

    const exportPDF = useExportPDFCommand({
        space,
        changeRequestId: changeRequest?.id,
        pageId: page.id,
    });

    const pageComputedDialogTrigger = useDialogTriggerState();
    const importDialogTrigger = useDialogTriggerState();

    //
    // Create the palette
    //
    const palette = useEventCallback(() => {
        const pageVisibility = hiveCore.isRevisionPageParent(page)
            ? hiveCore.getCachedPageVisibilityInRevision(revision, page.id)
            : null;

        return createPalette({
            position: {
                width: PalettePositionWidth.MEDIUM,
                maxHeight: PalettePositionMaxHeight.AUTO,
            },
            groups: [
                readOnly
                    ? null
                    : {
                          key: 'pageEdit',
                          commands: [
                              // Edit page title, icon, link title and slug
                              page.type !== hiveCore.RevisionPageType.Computed
                                  ? {
                                        key: 'pageEdit.edit',
                                        icon: Icon.Type,
                                        title:
                                            page.type === hiveCore.RevisionPageType.Link
                                                ? 'Edit'
                                                : 'Edit title & slug',
                                        trigger: () => {
                                            setModal('edit');
                                        },
                                    }
                                  : null,
                              // Add cover
                              page.type === hiveCore.RevisionPageType.Document &&
                              !hiveCore.hasPageCover(page)
                                  ? {
                                        key: 'pageEdit.addCover',
                                        icon: Icon.Image,
                                        title: 'Add cover',
                                        trigger: async () => {
                                            assertIsDefined(hiveBranch);

                                            navigateToPage(revision, page.id);

                                            await hiveBranch.change((change) => {
                                                change.addPageCover(page.id);
                                            });

                                            logEvent({
                                                ...analyticsContext,
                                                type: 'edit_add_cover',
                                            });
                                        },
                                    }
                                  : null,
                              // Duplicate
                              page.type !== hiveCore.RevisionPageType.Computed
                                  ? {
                                        key: 'pageEdit.duplicate',
                                        icon: Icon.PageDuplicate,
                                        title: 'Duplicate',
                                        trigger: async () => {
                                            assertIsDefined(hiveBranch);

                                            const newPageId = await hiveBranch.change((change) => {
                                                return change.duplicatePage({
                                                    originalPageId: page.id,
                                                });
                                            });

                                            if (!newPageId) {
                                                return;
                                            }

                                            logEvent(
                                                createPageEvent(space, page, {
                                                    ...analyticsContext,
                                                    type: 'edit_duplicate_page',
                                                    pageKind: page.type,
                                                })
                                            );

                                            if (page.type !== hiveCore.RevisionPageType.Link) {
                                                navigateToPage(hiveBranch.revision, newPageId);
                                            }
                                        },
                                    }
                                  : null,
                              // Page options
                              page.type === hiveCore.RevisionPageType.Document
                                  ? {
                                        key: 'pageEdit.options',
                                        icon: Icon.Options02,
                                        title: 'Options',
                                        trigger: () => {
                                            navigateToPage(revision, page.id, {
                                                sideSheet: {
                                                    type: 'page',
                                                },
                                            });
                                        },
                                    }
                                  : null,
                              // Insert
                              hiveCore.isRevisionPageParent(page)
                                  ? {
                                        key: 'pageEdit.insert',
                                        icon: Icon.AddPageGroup,
                                        title: 'Insert subpage',
                                        descendant: () => insertPalette,
                                        trigger: () => {
                                            insertPage((change) => {
                                                return hiveCore.createPageDocument({
                                                    title: change.getUniquePageTitle('Page', true),
                                                });
                                            });
                                        },
                                    }
                                  : null,
                              // Import
                              hiveCore.isRevisionPageParent(page)
                                  ? {
                                        key: 'pageEdit.import',
                                        icon: Icon.Download,
                                        title: 'Import subpages',
                                        trigger: () => {
                                            importDialogTrigger.open();
                                        },
                                    }
                                  : null,
                              // Edit computed content source
                              page.type === hiveCore.RevisionPageType.Computed &&
                              page.computed.type !== 'builtin:translation'
                                  ? {
                                        key: 'pageEdit.editContentSource',
                                        icon: Icon.Settings,
                                        title: 'Configure',
                                        trigger: () => {
                                            pageComputedDialogTrigger.open();
                                        },
                                    }
                                  : null,
                              // Delete
                              {
                                  key: 'pageEdit.delete',
                                  icon: Icon.Trashcan,
                                  title: 'Delete',
                                  trigger: () => {
                                      setModal('delete');
                                  },
                              },
                          ].filter(filterOutNullable),
                      },
                {
                    key: 'pageEditOptions',
                    commands: [
                        siteAudience ? siteAudience.command : null,
                        !readOnly && pageVisibility && hiveCore.isRevisionPageParent(page)
                            ? {
                                  type: PaletteCommandType.SWITCH as const,
                                  key: 'pageEditOptions.hidden',
                                  testID: 'page-options-hide-page-switch',
                                  icon: Icon.EyeOff,
                                  title: `Hide ${hiveCore.isRevisionPageDocument(page) ? 'page' : 'group'}`,
                                  disabled: pageVisibility.hidden.setFromAncestor,
                                  enabled: pageVisibility.hidden.value,
                                  trigger: async () => {
                                      const hidden = !page.hidden;
                                      assertIsDefined(hiveBranch);

                                      await hiveBranch.change((change) => {
                                          change.updatePageVisibility(page.id, {
                                              hidden,
                                          });
                                      });

                                      logEvent({
                                          ...analyticsContext,
                                          type: 'edit_toggle_hidden',
                                          hidden,
                                      });
                                  },
                              }
                            : null,
                        ...(publicPageCommands ? publicPageCommands : []),
                    ].filter(filterOutNullable),
                },
                {
                    key: 'pageRead',
                    commands: [
                        // Export as PDF
                        page.type === hiveCore.RevisionPageType.Document ? exportPDF : null,
                        // Copy URL
                        hiveCore.isRevisionPageParent(page)
                            ? {
                                  key: 'pageRead.copyURL',
                                  icon: Icon.Link,
                                  title: 'Copy link',
                                  trigger: () => {
                                      const fullPath = getNavigationHref(revision, page.id);
                                      const pageURL = `${window.location.origin}${fullPath}`;
                                      clipboard.writeText(pageURL, {
                                          notify: { title: 'Page URL copied to clipboard' },
                                      });
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable),
                },
            ].filter(filterOutNullable),
        });
    });

    //
    // Close the modal
    //
    const closeModal = () => {
        setModal(null);
    };

    const element = (
        <>
            {!readOnly ? (
                <>
                    {insertElement}
                    <EditPageDialog
                        isOpen={modal === 'edit'}
                        revision={revision}
                        page={page}
                        analyticsContext={analyticsContext}
                        onClose={closeModal}
                    />
                    <DeletePageDialog
                        isOpen={modal === 'delete'}
                        page={page}
                        onClose={closeModal}
                    />
                    <DialogRoot {...importDialogTrigger.rootProps}>
                        <ImportInSpaceDialog page={page.id} />
                    </DialogRoot>
                    <DialogRoot {...pageComputedDialogTrigger.rootProps}>
                        {page.type === hiveCore.RevisionPageType.Computed &&
                        page.computed.type !== 'builtin:translation' ? (
                            <EditContentSourceDialog
                                organizationId={space.organization}
                                spaceId={space.id}
                                page={page}
                                analyticsContext={analyticsContext}
                            />
                        ) : null}
                    </DialogRoot>
                </>
            ) : null}

            {siteAudience ? siteAudience.dialogs : null}
        </>
    );

    return (
        <Component
            {...props}
            commandsPalette={palette}
            commandsElement={element}
            siteAudience={siteAudience}
        />
    );
}
